if (!customElements.get('press-element')) {
  customElements.define(
    'press-element',
    class PressElement extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.testimonials = this.querySelector('.press-testimonials');
        this.thumbs = this.querySelector('.press-thumbs');
        this.sectionId = this.dataset.sectionId;
        this.testimonialsSlider = this.testimonials.querySelector('.swiper');
        this.thumbsSlider = this.thumbs.querySelector('.swiper');

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);
      
        window.addEventListener('resize', () => {
          this.updateGap();
          this.init(); 
        });
      
        this.updateGap();
        this.init();
      }

      init() {
        this.destroySlider();
        this.initSlider();
      }

      updateGap() {
        const columnGap = window.getComputedStyle(this.thumbs).getPropertyValue('--f-column-gap');
        this.columnGap = columnGap;
      }

      setSliderOptions() {
        const baseSpaceBetween = parseFloat((this.columnGap || '0rem').replace('rem', '')) * 10;
        const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile).matches;
        const itemWidthVariable = isMobile ? '--item-width-mobile' : '--item-width';
        const itemWidth = parseFloat(window.getComputedStyle(this.thumbs).getPropertyValue(itemWidthVariable)) || 0;
        const activeScale = parseFloat(window.getComputedStyle(this.thumbs).getPropertyValue('--press-active-scale')) || 1;
        const activeItemExtension = (itemWidth * Math.max(activeScale - 1, 0)) / 2;
        const spaceBetween = baseSpaceBetween + activeItemExtension;

        // Testimonials slider
        this.testimonialsOptions = {
          slidesPerView: 1,
          spaceBetween: 30,
          loop: true,
          threshold: 1,
          grabCursor: true,
        };
      
        // Thumbs slider
        this.thumbsOptions = {
          slidesPerView: 'auto',
          spaceBetween: spaceBetween,
          threshold: 1,
          // Free mode allows the wrapper to stop between slides, leaving the
          // active logo off-centre. Use normal slide snapping so the active
          // logo always returns to the centred snap point after a swipe.
          freeMode: false,
          centeredSlides: true,
          slideToClickedSlide: false,
          watchSlidesProgress: true,
          watchSlidesVisibility: true,
          loop: true,
        };
      }

      initSlider() {
        this.setSliderOptions();

        this.thumbsInstance = new FoxTheme.Carousel(this.thumbsSlider, this.thumbsOptions);
        this.thumbsInstance.init();

        this.sliderInstance = new FoxTheme.Carousel(this.testimonialsSlider, this.testimonialsOptions);
        this.sliderInstance.init();

        const thumbsSlider = this.thumbsInstance.slider;
        const testimonialsSlider = this.sliderInstance.slider;
        let isSyncingThumbs = false;
        let isSyncingTestimonials = false;
        let isHandlingThumbClick = false;

        const getSlideCount = (swiper) =>
          Array.from(swiper.slides).filter(
            (slide) => !slide.classList.contains('swiper-slide-duplicate')
          ).length;

        const getSlideIndexInDirection = (swiper, targetIndex, direction) => {
          const matchingIndexes = Array.from(swiper.slides).reduce((indexes, slide, index) => {
            if (Number(slide.dataset.swiperSlideIndex) === targetIndex) indexes.push(index);
            return indexes;
          }, []);

          if (direction === 'next') {
            return matchingIndexes.find((index) => index > swiper.activeIndex) ?? null;
          }

          if (direction === 'prev') {
            return matchingIndexes.reverse().find((index) => index < swiper.activeIndex) ?? null;
          }

          return null;
        };

        const getMovementDirection = (swiper) => {
          if (swiper.activeIndex === swiper.previousIndex) return null;
          return swiper.activeIndex > swiper.previousIndex ? 'next' : 'prev';
        };

        const updateThumbActiveState = (targetIndex) => {
          thumbsSlider.slides.forEach((slide) => {
            slide.classList.toggle(
              'swiper-slide-thumb-active',
              Number(slide.dataset.swiperSlideIndex) === targetIndex
            );
          });
        };

        thumbsSlider.on('slideChangeTransitionStart', () => {
          thumbsSlider.slides.forEach((slide) => {
            slide.classList.remove('press-loop-fix-active');
          });
        });

        const syncTestimonialsWithThumbs = (targetIndex, direction) => {
          if (!Number.isInteger(targetIndex) || testimonialsSlider.realIndex === targetIndex) return;

          // A logo click supplies the exact physical direction it travelled.
          // Use the matching visible clone in the testimonial slider too,
          // instead of letting slideNext/slidePrev choose a separate loop path.
          // Correct a previous clone before starting the next visible movement;
          // correcting the left-edge clone after its animation is what caused
          // the visible reverse jump.
          if (direction) {
            testimonialsSlider.loopFix();
            testimonialsSlider._clientLeft = testimonialsSlider.wrapperEl.clientLeft;
          }

          const directionalSlideIndex = getSlideIndexInDirection(
            testimonialsSlider,
            targetIndex,
            direction
          );
          if (directionalSlideIndex !== null) {
            testimonialsSlider.slideTo(directionalSlideIndex);
            return;
          }

          // Direction is known, but this target has no matching clone on that
          // side. Preserve the physical movement rather than falling back to a
          // real-index calculation that could choose the opposite direction.
          if (direction === 'next') {
            testimonialsSlider.slideNext();
            return;
          }
          if (direction === 'prev') {
            testimonialsSlider.slidePrev();
            return;
          }

          const slideCount = getSlideCount(testimonialsSlider);

          if (!slideCount) return;

          const nextIndex = (testimonialsSlider.realIndex + 1) % slideCount;
          const previousIndex = (testimonialsSlider.realIndex - 1 + slideCount) % slideCount;

          // Let Swiper move one step when the logos move to an adjacent item.
          // This is important at the last -> first boundary: slideNext() moves
          // into the adjacent loop clone and keeps the text animation continuous.
          if (targetIndex === nextIndex) {
            testimonialsSlider.slideNext();
          } else if (targetIndex === previousIndex) {
            testimonialsSlider.slidePrev();
          } else {
            testimonialsSlider.slideToLoop(targetIndex);
          }
        };

        const syncThumbsWithTestimonials = (targetIndex, direction) => {
          if (!Number.isInteger(targetIndex) || thumbsSlider.realIndex === targetIndex) return;

          if (direction) {
            thumbsSlider.loopFix();
            thumbsSlider._clientLeft = thumbsSlider.wrapperEl.clientLeft;

            const directionalSlideIndex = getSlideIndexInDirection(
              thumbsSlider,
              targetIndex,
              direction
            );
            if (directionalSlideIndex !== null) {
              thumbsSlider.slideTo(directionalSlideIndex);
              return;
            }

            if (direction === 'next') {
              thumbsSlider.slideNext();
            } else {
              thumbsSlider.slidePrev();
            }
            return;
          }

          const slideCount = getSlideCount(thumbsSlider);
          if (!slideCount) return;

          const nextIndex = (thumbsSlider.realIndex + 1) % slideCount;
          const previousIndex = (thumbsSlider.realIndex - 1 + slideCount) % slideCount;

          if (targetIndex === nextIndex) {
            thumbsSlider.slideNext();
          } else if (targetIndex === previousIndex) {
            thumbsSlider.slidePrev();
          } else {
            thumbsSlider.slideToLoop(targetIndex);
          }
        };

        this.thumbsInstance.slider.on('realIndexChange', (swiper) => {
          updateThumbActiveState(swiper.realIndex);
          if (isSyncingThumbs || isHandlingThumbClick) return;

          isSyncingTestimonials = true;
          syncTestimonialsWithThumbs(swiper.realIndex, getMovementDirection(swiper));
          isSyncingTestimonials = false;
        });

        testimonialsSlider.on('realIndexChange', (swiper) => {
          updateThumbActiveState(swiper.realIndex);
          if (isSyncingTestimonials) return;

          isSyncingThumbs = true;
          syncThumbsWithTestimonials(swiper.realIndex, getMovementDirection(swiper));
          isSyncingThumbs = false;
        });

        this.thumbClickHandler = (event) => {
          const swiper = thumbsSlider;
          const clickedSlide = event.target.closest?.('.swiper-slide');
          if (!clickedSlide || !thumbsSlider.el.contains(clickedSlide)) return;

          event.stopImmediatePropagation();

          const targetIndex = Number.parseInt(clickedSlide.dataset.swiperSlideIndex, 10);
          const clickedIndex = Array.prototype.indexOf.call(swiper.slides, clickedSlide);
          if (!Number.isInteger(targetIndex) || clickedIndex < 0) return;
          const direction = clickedIndex > swiper.activeIndex ? 'next' : clickedIndex < swiper.activeIndex ? 'prev' : null;

          // In the Shopify editor, clicking a block also emits
          // shopify:block:select after this handler. Ignore that editor-only
          // event so it cannot issue a second slideToLoop() and reverse the
          // direction selected by the customer.
          this.ignoreBlockSelect = true;
          window.clearTimeout(this.ignoreBlockSelectTimer);
          this.ignoreBlockSelectTimer = window.setTimeout(() => {
            this.ignoreBlockSelect = false;
          }, 2000);

          // Move the exact visible logo that was clicked. This may be a loop
          // clone at either edge, which is intentional: it gives the click the
          // same continuous direction as a drag and centres that logo.
          const resetLoopPosition = () => {
            if (!swiper.destroyed) {
              const isLoopEdge =
                swiper.activeIndex < swiper.loopedSlides ||
                swiper.activeIndex >= swiper.slides.length - swiper.loopedSlides;

              // loopFix uses slideTo(..., 0) internally and may emit a
              // realIndexChange. It is only a clone correction, never a user
              // navigation, so it must not drive the testimonial slider.
              isSyncingThumbs = true;
              swiper.loopFix();
              swiper._clientLeft = swiper.wrapperEl.clientLeft;
              isSyncingThumbs = false;

              // loopFix swaps the active clone for its original counterpart.
              // Keep that equivalent slide from replaying the active-logo
              // animation; the marker is cleared at the next real movement.
              if (isLoopEdge) {
                swiper.slides[swiper.activeIndex]?.classList.add('press-loop-fix-active');
              }
            }
          };

          // Register before slideTo() so a zero-duration transition cannot
          // finish before the loop correction listener is attached.
          swiper.once('transitionEnd', resetLoopPosition);
          isHandlingThumbClick = true;
          const moved = swiper.slideTo(clickedIndex);
          isHandlingThumbClick = false;

          // slideTo() does not run loopFix itself. Once the movement finishes,
          // swap an edge clone for its identical original at zero duration so
          // the following click starts from a valid loop position.
          if (!moved) swiper.off('transitionEnd', resetLoopPosition);

          isSyncingTestimonials = true;
          syncTestimonialsWithThumbs(targetIndex, direction);
          isSyncingTestimonials = false;
        };
        thumbsSlider.el.addEventListener('click', this.thumbClickHandler, true);

        updateThumbActiveState(testimonialsSlider.realIndex);

        //shopify desgin mode
        if (Shopify.designMode && typeof this.sliderInstance === 'object') {
          this.blockSelectHandler = (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            if (this.ignoreBlockSelect) {
              this.ignoreBlockSelect = false;
              window.clearTimeout(this.ignoreBlockSelectTimer);
              return;
            }

            let { target } = e;
            const index = Number(target.dataset.index);
            if (!Number.isInteger(index)) return;

            this.sliderInstance.slider.slideToLoop(index);
          };
          document.addEventListener('shopify:block:select', this.blockSelectHandler);
        }
      }

      destroySlider() {
        if (this.thumbClickHandler && this.thumbsInstance?.slider?.el) {
          this.thumbsInstance.slider.el.removeEventListener('click', this.thumbClickHandler, true);
          this.thumbClickHandler = null;
        }

        if (this.blockSelectHandler) {
          document.removeEventListener('shopify:block:select', this.blockSelectHandler);
          this.blockSelectHandler = null;
        }

        window.clearTimeout(this.ignoreBlockSelectTimer);
        this.ignoreBlockSelect = false;

        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }

        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy();
          this.thumbsInstance = false;
        }
      }
    }
  );
}
